import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { 
  Package, CheckCircle, Clock, Calendar, 
  TrendingDown, DollarSign, ClipboardCheck, AlertCircle, Save,
  Filter, ArrowDownWideNarrow, BarChart3, Download, RefreshCw
} from 'lucide-react';
import { getErrorMessage } from '../lib/utils';

import { API } from '@/lib/api';
import { formatCurrency } from '@/lib/utils';


const getStatusColor = (status) => {
  switch (status) {
    case 'approved': return 'bg-green-100 text-green-800';
    case 'submitted': return 'bg-blue-100 text-blue-800';
    case 'draft': return 'bg-gray-100 text-gray-800';
    default: return 'bg-gray-100 text-gray-800';
  }
};

/**
 * Reusable Inventory Master Component
 * Used by Bar, Kitchen, and Cafe masters
 * 
 * @param {Object} props
 * @param {Object} props.user - Current user object
 * @param {string} props.masterType - 'bar' | 'kitchen' | 'cafe'
 * @param {string} props.apiPrefix - API endpoint prefix (e.g., 'bar-master')
 * @param {string} props.superCategory - 'Liquor' | 'Food' | 'Normal-Drinks'
 * @param {string} props.userRole - Role that can manage this master
 * @param {string} props.title - Display title
 * @param {React.Component} props.Icon - Icon component to display
 * @param {string} props.iconColor - Tailwind color class for icon
 * @param {string} props.emptyMessage - Message when no items found
 */
export default function InventoryMaster({ 
  user, 
  masterType,
  apiPrefix,
  superCategory,
  userRole,
  title,
  Icon,
  iconColor = 'text-purple-600',
  emptyMessage = 'No approved items found'
}) {
  const [activeTab, setActiveTab] = useState('daily');
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [lastRefresh, setLastRefresh] = useState(new Date());
  
  // Data
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [stockEntries, setStockEntries] = useState([]);
  const [dailyClosings, setDailyClosings] = useState([]);
  const [summary, setSummary] = useState({});
  const [pendingStock, setPendingStock] = useState([]);
  const [pendingCount, setPendingCount] = useState(0);
  const [openingStocks, setOpeningStocks] = useState([]);
  
  // Filters
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().slice(0, 10));
  const [dateRange, setDateRange] = useState({ start: '', end: '' });
  
  // Admin Report Filters
  const [reportDateRange, setReportDateRange] = useState({ 
    start: new Date(new Date().setDate(1)).toISOString().slice(0, 10),
    end: new Date().toISOString().slice(0, 10) 
  });
  const [filterCategory, setFilterCategory] = useState('all');
  const [sortBy, setSortBy] = useState('category');
  const [reportData, setReportData] = useState([]);
  const [reportSummary, setReportSummary] = useState({});
  
  // Form state
  const [stockForm, setStockForm] = useState({});
  const [openingStockForm, setOpeningStockForm] = useState({});
  
  const isUserRole = user?.role === userRole;
  const isAdmin = user?.role === 'admin';
  const canManage = isUserRole || isAdmin;

  const fetchInitialData = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    setRefreshing(true);
    try {
      const token = localStorage.getItem('token');
      const [itemsRes, catsRes, summaryRes] = await Promise.all([
        axios.get(`${API}/${apiPrefix}/items`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/item-categories`, { headers: { Authorization: `Bearer ${token}` }, params: { super_category: superCategory } }),
        axios.get(`${API}/${apiPrefix}/summary`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setItems(itemsRes.data);
      setCategories(catsRes.data);
      setSummary(summaryRes.data);
      setLastRefresh(new Date());
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [apiPrefix, superCategory]);

  useEffect(() => {
    fetchInitialData();
    fetchPendingStock();
    fetchOpeningStocks();
  }, [fetchInitialData]);

  // Auto-refresh every 60 seconds
  useEffect(() => {
    const interval = setInterval(() => fetchInitialData(true), 60000);
    return () => clearInterval(interval);
  }, [fetchInitialData]);

  const handleManualRefresh = () => {
    fetchInitialData(true);
    fetchPendingStock();
    toast.success('Data refreshed!');
  };

  useEffect(() => {
    if (selectedDate && items.length > 0) {
      fetchStockForDate(selectedDate);
    }
  }, [selectedDate, items]);

  useEffect(() => {
    if (activeTab === 'history') {
      fetchDailyClosings();
    }
    if (activeTab === 'reports' && isAdmin) {
      fetchReportData();
    }
  }, [activeTab, dateRange, reportDateRange, filterCategory, sortBy]);

  const fetchPendingStock = async () => {
    try {
      const token = localStorage.getItem('token');
      const [countRes, issuesRes] = await Promise.all([
        axios.get(`${API}/store-ledger/pending-issues/count`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/store-ledger/pending-issues`, { 
          headers: { Authorization: `Bearer ${token}` },
          params: { status: 'pending' }
        })
      ]);
      setPendingCount(countRes.data[masterType] || 0);
      setPendingStock(issuesRes.data || []);
    } catch (error) {
      console.error('Failed to fetch pending stock:', error);
    }
  };

  const acknowledgeStock = async (issueId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/store-ledger/pending-issues/${issueId}/acknowledge`, null, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Stock acknowledged and added to ledger');
      fetchPendingStock();
      fetchStockForDate(selectedDate);
      fetchInitialData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to acknowledge stock'));
    }
  };

  const rejectStock = async (issueId, reason) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/store-ledger/pending-issues/${issueId}/reject`, null, {
        headers: { Authorization: `Bearer ${token}` },
        params: { reason }
      });
      toast.success('Stock issue rejected and returned to store');
      fetchPendingStock();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to reject stock'));
    }
  };

  const acknowledgeAllStock = async () => {
    if (pendingStock.length === 0) return;
    
    try {
      const token = localStorage.getItem('token');
      const issueIds = pendingStock.map(p => p.id);
      const response = await axios.post(`${API}/store-ledger/pending-issues/bulk-acknowledge`, issueIds, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(response.data.message);
      fetchPendingStock();
      fetchStockForDate(selectedDate);
      fetchInitialData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to acknowledge stock'));
    }
  };

  const fetchOpeningStocks = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/${apiPrefix}/opening-stock`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOpeningStocks(response.data || []);
      
      const form = {};
      response.data.forEach(os => {
        form[os.item_id] = os.opening_stock || 0;
      });
      setOpeningStockForm(form);
    } catch (error) {
      console.error('Failed to fetch opening stocks:', error);
    }
  };

  const saveOpeningStock = async (itemId) => {
    try {
      const token = localStorage.getItem('token');
      const openingStock = openingStockForm[itemId] || 0;
      
      await axios.post(`${API}/${apiPrefix}/opening-stock`, null, {
        headers: { Authorization: `Bearer ${token}` },
        params: { item_id: itemId, opening_stock: openingStock }
      });
      
      toast.success('Opening stock saved');
      fetchOpeningStocks();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to save opening stock'));
    }
  };

  const saveAllOpeningStock = async () => {
    try {
      const token = localStorage.getItem('token');
      const itemsToSave = items.map(item => ({
        item_id: item.id,
        opening_stock: parseFloat(openingStockForm[item.id]) || 0
      }));
      
      const response = await axios.post(`${API}/${apiPrefix}/opening-stock/bulk`, itemsToSave, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(response.data.message);
      fetchOpeningStocks();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to save opening stock'));
    }
  };

  const fetchStockForDate = async (date) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/${apiPrefix}/stock/date/${date}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setStockEntries(response.data.entries || []);
      
      const newForm = {};
      items.forEach(item => {
        newForm[item.id] = { opening_stock: 0, added_stock: 0, closing_stock: 0 };
      });
      
      (response.data.entries || []).forEach(entry => {
        newForm[entry.item_id] = {
          opening_stock: entry.opening_stock,
          added_stock: entry.added_stock,
          closing_stock: entry.closing_stock,
          id: entry.id,
          status: entry.status,
          consumption: entry.consumption,
          consumption_value: entry.consumption_value,
          total_value: entry.total_value,
          store_acknowledged: entry.store_acknowledged
        };
      });
      setStockForm(newForm);
    } catch (error) {
      toast.error('Failed to fetch stock data');
    }
  };

  const fetchDailyClosings = async () => {
    try {
      const token = localStorage.getItem('token');
      const params = {};
      if (dateRange.start) params.start_date = dateRange.start;
      if (dateRange.end) params.end_date = dateRange.end;
      
      const response = await axios.get(`${API}/${apiPrefix}/closings`, {
        headers: { Authorization: `Bearer ${token}` },
        params
      });
      setDailyClosings(response.data);
    } catch (error) {
      toast.error('Failed to fetch daily closings');
    }
  };

  const fetchReportData = async () => {
    try {
      const token = localStorage.getItem('token');
      const params = {
        start_date: reportDateRange.start,
        end_date: reportDateRange.end,
        sort_by: sortBy
      };
      if (filterCategory !== 'all') params.category_id = filterCategory;
      
      const response = await axios.get(`${API}/${apiPrefix}/reports/consumption`, {
        headers: { Authorization: `Bearer ${token}` },
        params
      });
      setReportData(response.data.items || []);
      setReportSummary(response.data.summary || {});
    } catch (error) {
      toast.error('Failed to fetch report data');
    }
  };

  const handleStockChange = (itemId, field, value) => {
    setStockForm(prev => ({
      ...prev,
      [itemId]: { ...prev[itemId], [field]: parseFloat(value) || 0 }
    }));
  };

  const saveStockEntry = async (itemId) => {
    try {
      const token = localStorage.getItem('token');
      const item = items.find(i => i.id === itemId);
      const formData = stockForm[itemId];
      
      await axios.post(`${API}/${apiPrefix}/stock`, {
        date: selectedDate,
        item_id: itemId,
        opening_stock: formData.opening_stock || 0,
        added_stock: formData.added_stock || 0,
        closing_stock: formData.closing_stock || 0
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(`Stock saved for ${item.item_name}`);
      fetchStockForDate(selectedDate);
      fetchInitialData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to save stock'));
    }
  };

  const autoFillOpening = async () => {
    try {
      const token = localStorage.getItem('token');
      const newForm = { ...stockForm };
      
      for (const item of items) {
        const response = await axios.get(`${API}/${apiPrefix}/stock/previous/${item.id}`, {
          headers: { Authorization: `Bearer ${token}` },
          params: { date: selectedDate }
        });
        newForm[item.id] = { 
          ...newForm[item.id], 
          opening_stock: response.data.closing_stock || 0 
        };
      }
      
      setStockForm(newForm);
      toast.success('Opening stock auto-filled from previous entries');
    } catch (error) {
      toast.error('Failed to auto-fill opening stock');
    }
  };

  const submitDailyClosing = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/${apiPrefix}/closings/submit`, { date: selectedDate }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Daily closing submitted for approval');
      fetchInitialData();
      fetchStockForDate(selectedDate);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to submit daily closing'));
    }
  };

  const approveClosing = async (closingId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/${apiPrefix}/closings/${closingId}/approve`, null, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Daily closing approved');
      fetchDailyClosings();
      fetchInitialData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to approve closing'));
    }
  };

  const unlockClosing = async (closingId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/${apiPrefix}/closings/${closingId}/unlock`, null, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Daily closing unlocked');
      fetchDailyClosings();
      fetchInitialData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to unlock closing'));
    }
  };

  // Group items by category
  const groupedItems = categories.map(cat => ({
    ...cat,
    items: items.filter(item => item.category_id === cat.id)
  })).filter(cat => cat.items.length > 0);

  const todayClosing = dailyClosings.find(c => c.date === selectedDate);
  const isDateLocked = todayClosing?.status === 'approved' || todayClosing?.status === 'submitted';
  const canSubmit = stockEntries?.length > 0 && summary.today_closing_status !== 'submitted';

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Refresh Bar */}
      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-500">
          Last updated: {lastRefresh.toLocaleTimeString('en-IN')}
          {refreshing && <span className="ml-2 text-blue-500">• Refreshing...</span>}
        </p>
        <Button variant="outline" size="sm" onClick={handleManualRefresh} disabled={refreshing} className="gap-2">
          <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Header */}
      <div className="flex flex-wrap justify-between items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Icon className={`w-7 h-7 ${iconColor}`} />
            {title}
          </h1>
          <p className="text-gray-500 text-sm mt-1">
            Manage daily {superCategory.toLowerCase()} inventory and consumption
          </p>
        </div>
        
        {canManage && !isDateLocked && (
          <Button onClick={submitDailyClosing} disabled={!canSubmit} data-testid="submit-closing-btn">
            <ClipboardCheck className="w-4 h-4 mr-2" />
            Submit Daily Closing
          </Button>
        )}
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Total Items</p>
                <p className="text-2xl font-bold">{items.length}</p>
              </div>
              <Package className={`w-8 h-8 ${iconColor} opacity-50`} />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Categories</p>
                <p className="text-2xl font-bold">{categories.length}</p>
              </div>
              <Filter className="w-8 h-8 text-blue-600 opacity-50" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Today Consumption</p>
                <p className="text-2xl font-bold text-red-600">{formatCurrency(summary.today_consumption_value)}</p>
              </div>
              <TrendingDown className="w-8 h-8 text-red-600 opacity-50" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Today Status</p>
                <Badge className={getStatusColor(summary.today_closing_status || 'not_started')}>
                  {summary.today_closing_status === 'approved' ? 'Approved' : 
                   summary.today_closing_status === 'submitted' ? 'Submitted' : 'Draft'}
                </Badge>
              </div>
              <Clock className="w-8 h-8 text-gray-400 opacity-50" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Content */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="daily">Daily Stock Entry</TabsTrigger>
          {canManage && (
            <TabsTrigger value="opening-stock" data-testid="opening-stock-tab">
              Opening Stock
            </TabsTrigger>
          )}
          {canManage && (
            <TabsTrigger value="pending" data-testid="pending-stock-tab">
              Pending from Store
              {pendingCount > 0 && (
                <Badge variant="destructive" className="ml-2 h-5 px-1.5 text-xs animate-pulse">
                  {pendingCount}
                </Badge>
              )}
            </TabsTrigger>
          )}
          <TabsTrigger value="history">
            Closing History
            {summary.pending_approvals > 0 && isAdmin && (
              <Badge variant="destructive" className="ml-2 h-5 px-1.5 text-xs">
                {summary.pending_approvals}
              </Badge>
            )}
          </TabsTrigger>
          {isAdmin && (
            <TabsTrigger value="reports">
              <BarChart3 className="w-4 h-4 mr-1" />
              Consumption Reports
            </TabsTrigger>
          )}
        </TabsList>

        {/* Opening Stock Tab */}
        {canManage && (
          <TabsContent value="opening-stock" className="mt-4">
            <Card>
              <CardHeader>
                <div className="flex justify-between items-center">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <Package className="w-5 h-5 text-blue-600" />
                      Initial Opening Stock
                    </CardTitle>
                    <p className="text-sm text-gray-500 mt-1">
                      Set one-time opening stock for items. After this, opening stock will auto-fill from previous closing.
                    </p>
                  </div>
                  <Button onClick={saveAllOpeningStock} data-testid="save-all-opening-btn">
                    <Save className="w-4 h-4 mr-2" />
                    Save All Opening Stock
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {items.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <Icon className="w-12 h-12 mx-auto mb-2 text-gray-400" />
                    <p>{emptyMessage}</p>
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Item</TableHead>
                        <TableHead>Category</TableHead>
                        <TableHead>Company</TableHead>
                        <TableHead>Unit</TableHead>
                        <TableHead className="text-center w-[120px]">Opening Stock</TableHead>
                        <TableHead className="w-[100px]">Action</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {items.map((item) => {
                        const existingOpening = openingStocks.find(os => os.item_id === item.id);
                        return (
                          <TableRow key={item.id} className={existingOpening ? 'bg-green-50' : ''}>
                            <TableCell className="font-medium">{item.item_name}</TableCell>
                            <TableCell><Badge variant="outline">{item.category_name}</Badge></TableCell>
                            <TableCell>{item.company}</TableCell>
                            <TableCell>{item.unit}</TableCell>
                            <TableCell className="text-center">
                              <Input
                                type="number"
                                step="0.01"
                                value={openingStockForm[item.id] || ''}
                                onChange={(e) => setOpeningStockForm({ ...openingStockForm, [item.id]: e.target.value })}
                                className="w-24 text-center"
                                placeholder="0"
                              />
                            </TableCell>
                            <TableCell>
                              <Button size="sm" variant="outline" onClick={() => saveOpeningStock(item.id)}>
                                <Save className="w-4 h-4" />
                              </Button>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        )}

        {/* Pending Stock Tab */}
        {canManage && (
          <TabsContent value="pending" className="mt-4">
            <Card>
              <CardHeader>
                <div className="flex justify-between items-center">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <Package className="w-5 h-5 text-amber-600" />
                      Pending Stock from Store
                    </CardTitle>
                    <p className="text-sm text-gray-500 mt-1">
                      Stock issued from store needs your acknowledgment before it is added to your ledger
                    </p>
                  </div>
                  {pendingStock.length > 0 && (
                    <Button onClick={acknowledgeAllStock} data-testid="acknowledge-all-btn">
                      <CheckCircle className="w-4 h-4 mr-2" />
                      Acknowledge All ({pendingStock.length})
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                {pendingStock.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <CheckCircle className="w-12 h-12 mx-auto mb-2 text-green-400" />
                    <p>No pending stock to acknowledge</p>
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Date</TableHead>
                        <TableHead>Item</TableHead>
                        <TableHead>Category</TableHead>
                        <TableHead className="text-right">Quantity</TableHead>
                        <TableHead className="text-right">Unit Price</TableHead>
                        <TableHead className="text-right">Value</TableHead>
                        <TableHead>Issued By</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {pendingStock.map((item) => (
                        <TableRow key={item.id} className="bg-amber-50">
                          <TableCell>{item.date}</TableCell>
                          <TableCell className="font-medium">{item.item_name}</TableCell>
                          <TableCell><Badge variant="outline">{item.category_name}</Badge></TableCell>
                          <TableCell className="text-right font-mono">
                            {item.quantity?.toFixed(2)} {item.unit}
                          </TableCell>
                          <TableCell className="text-right">{formatCurrency(item.unit_price)}</TableCell>
                          <TableCell className="text-right font-medium">{formatCurrency(item.total_value)}</TableCell>
                          <TableCell className="text-sm text-gray-600">{item.issued_by_name}</TableCell>
                          <TableCell className="text-right">
                            <div className="flex justify-end gap-2">
                              <Button size="sm" onClick={() => acknowledgeStock(item.id)}>
                                <CheckCircle className="w-4 h-4 mr-1" />
                                Accept
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                className="text-red-600 hover:text-red-700"
                                onClick={() => {
                                  const reason = prompt('Enter reason for rejection:');
                                  if (reason) rejectStock(item.id, reason);
                                }}
                              >
                                Reject
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        )}

        {/* Daily Stock Entry Tab */}
        <TabsContent value="daily" className="mt-4">
          <Card>
            <CardHeader>
              <div className="flex flex-wrap justify-between items-center gap-4">
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2">
                    <Calendar className="w-4 h-4" />
                    <Label>Date:</Label>
                    <Input
                      type="date"
                      value={selectedDate}
                      onChange={(e) => setSelectedDate(e.target.value)}
                      className="w-44"
                      data-testid="date-selector"
                    />
                  </div>
                  {isDateLocked && (
                    <Badge className={getStatusColor(todayClosing?.status)}>
                      {todayClosing?.status === 'approved' ? 'Approved' : 'Submitted'}
                    </Badge>
                  )}
                </div>
                <div className="flex gap-2">
                  {canManage && !isDateLocked && (
                    <Button variant="outline" onClick={autoFillOpening} data-testid="auto-fill-btn">
                      <ArrowDownWideNarrow className="w-4 h-4 mr-2" />
                      Auto-fill Opening
                    </Button>
                  )}
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {/* Info about pending stock */}
              {pendingCount > 0 && !isDateLocked && (
                <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-center gap-3">
                  <Package className="w-5 h-5 text-amber-600 flex-shrink-0" />
                  <div className="text-sm text-amber-800">
                    <strong>{pendingCount} item{pendingCount > 1 ? 's' : ''} pending from Store.</strong> Go to{' '}
                    <button onClick={() => setActiveTab('pending')} className="underline font-medium">
                      Pending from Store
                    </button>{' '}
                    tab to acknowledge before closing.
                  </div>
                </div>
              )}
              
              {/* Info about Added Stock */}
              {!isDateLocked && (
                <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-800">
                  <strong>Note:</strong> Added Stock is automatically filled when you acknowledge items issued from Store. You cannot manually enter added stock.
                </div>
              )}
              
              {items.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Icon className="w-12 h-12 mx-auto mb-2 text-gray-400" />
                  <p>{emptyMessage}</p>
                  <p className="text-sm mt-1">Add items under the {superCategory} category and get them approved first</p>
                </div>
              ) : (
                <div className="space-y-6">
                  {groupedItems.map((category) => (
                    <div key={category.id}>
                      <h3 className="font-semibold text-lg mb-3 flex items-center gap-2">
                        <Icon className={`w-5 h-5 ${iconColor}`} />
                        {category.name}
                        <Badge variant="outline" className="ml-2">{category.items.length} items</Badge>
                      </h3>
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Item</TableHead>
                            <TableHead>Company</TableHead>
                            <TableHead>Unit</TableHead>
                            <TableHead className="text-center">Opening</TableHead>
                            <TableHead className="text-center">Added</TableHead>
                            <TableHead className="text-center">Closing</TableHead>
                            <TableHead className="text-center">Consumption</TableHead>
                            <TableHead className="text-right">Value</TableHead>
                            {canManage && !isDateLocked && <TableHead className="w-20">Action</TableHead>}
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {category.items.map((item) => {
                            const formEntry = stockForm[item.id] || {};
                            const opening = formEntry.opening_stock || 0;
                            const added = formEntry.added_stock || 0;
                            const closing = formEntry.closing_stock || 0;
                            const consumption = opening + added - closing;
                            
                            return (
                              <TableRow key={item.id}>
                                <TableCell className="font-medium">{item.item_name}</TableCell>
                                <TableCell>{item.company}</TableCell>
                                <TableCell>{item.unit}</TableCell>
                                <TableCell className="text-center">
                                  {isDateLocked ? (
                                    <span>{opening}</span>
                                  ) : (
                                    <Input
                                      type="number"
                                      step="0.01"
                                      value={opening || ''}
                                      onChange={(e) => handleStockChange(item.id, 'opening_stock', e.target.value)}
                                      className="w-20 text-center"
                                      disabled={!canManage}
                                    />
                                  )}
                                </TableCell>
                                <TableCell className="text-center">
                                  {isDateLocked ? (
                                    <span>{added}</span>
                                  ) : (
                                    <div className="relative group">
                                      <Input
                                        type="number"
                                        step="0.01"
                                        value={added || ''}
                                        className="w-20 text-center bg-gray-100 cursor-not-allowed"
                                        disabled={true}
                                        title="Auto-filled from acknowledged store issues"
                                      />
                                      {(added > 0 || formEntry.store_acknowledged) && (
                                        <span className="absolute -top-1 -right-1 w-3 h-3 bg-green-500 rounded-full" title="From Store"></span>
                                      )}
                                    </div>
                                  )}
                                </TableCell>
                                <TableCell className="text-center">
                                  {isDateLocked ? (
                                    <span>{closing}</span>
                                  ) : (
                                    <Input
                                      type="number"
                                      step="0.01"
                                      value={closing || ''}
                                      onChange={(e) => handleStockChange(item.id, 'closing_stock', e.target.value)}
                                      className="w-20 text-center"
                                      disabled={!canManage}
                                    />
                                  )}
                                </TableCell>
                                <TableCell className={`text-center font-medium ${consumption < 0 ? 'text-red-600' : ''}`}>
                                  {consumption.toFixed(2)}
                                </TableCell>
                                <TableCell className="text-right font-medium">
                                  {formatCurrency(formEntry.consumption_value || 0)}
                                </TableCell>
                                {canManage && !isDateLocked && (
                                  <TableCell>
                                    <Button size="sm" variant="outline" onClick={() => saveStockEntry(item.id)}>
                                      <Save className="w-4 h-4" />
                                    </Button>
                                  </TableCell>
                                )}
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Closing History Tab */}
        <TabsContent value="history" className="mt-4">
          <Card>
            <CardHeader>
              <div className="flex flex-wrap justify-between items-center gap-4">
                <CardTitle>Daily Closing History</CardTitle>
                <div className="flex items-center gap-2">
                  <Input
                    type="date"
                    value={dateRange.start}
                    onChange={(e) => setDateRange({ ...dateRange, start: e.target.value })}
                    className="w-40"
                  />
                  <span>to</span>
                  <Input
                    type="date"
                    value={dateRange.end}
                    onChange={(e) => setDateRange({ ...dateRange, end: e.target.value })}
                    className="w-40"
                  />
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {dailyClosings.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Clock className="w-12 h-12 mx-auto mb-2 text-gray-400" />
                  <p>No daily closings found</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date</TableHead>
                      <TableHead className="text-right">Items</TableHead>
                      <TableHead className="text-right">Consumption Value</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Submitted By</TableHead>
                      <TableHead>Approved By</TableHead>
                      {isAdmin && <TableHead className="text-right">Actions</TableHead>}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {dailyClosings.map((closing) => (
                      <TableRow key={closing.id}>
                        <TableCell className="font-medium">{closing.date}</TableCell>
                        <TableCell className="text-right">{closing.total_items}</TableCell>
                        <TableCell className="text-right text-red-600 font-medium">
                          {formatCurrency(closing.total_consumption_value)}
                        </TableCell>
                        <TableCell>
                          <Badge className={getStatusColor(closing.status)}>{closing.status}</Badge>
                        </TableCell>
                        <TableCell>{closing.submitted_by_name || '-'}</TableCell>
                        <TableCell>{closing.approved_by_name || '-'}</TableCell>
                        {isAdmin && (
                          <TableCell className="text-right">
                            {closing.status === 'submitted' && (
                              <Button size="sm" onClick={() => approveClosing(closing.id)}>
                                <CheckCircle className="w-4 h-4 mr-1" />
                                Approve
                              </Button>
                            )}
                            {closing.status === 'approved' && (
                              <Button size="sm" variant="outline" onClick={() => unlockClosing(closing.id)}>
                                Unlock
                              </Button>
                            )}
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Reports Tab (Admin Only) */}
        {isAdmin && (
          <TabsContent value="reports" className="mt-4">
            <Card>
              <CardHeader>
                <div className="flex flex-wrap justify-between items-center gap-4">
                  <CardTitle className="flex items-center gap-2">
                    <BarChart3 className="w-5 h-5" />
                    Consumption Reports
                  </CardTitle>
                  <div className="flex flex-wrap items-center gap-3">
                    <Input
                      type="date"
                      value={reportDateRange.start}
                      onChange={(e) => setReportDateRange({ ...reportDateRange, start: e.target.value })}
                      className="w-40"
                    />
                    <span>to</span>
                    <Input
                      type="date"
                      value={reportDateRange.end}
                      onChange={(e) => setReportDateRange({ ...reportDateRange, end: e.target.value })}
                      className="w-40"
                    />
                    <Select value={filterCategory} onValueChange={setFilterCategory}>
                      <SelectTrigger className="w-40"><SelectValue placeholder="Category" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Categories</SelectItem>
                        {categories.map(cat => (
                          <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Select value={sortBy} onValueChange={setSortBy}>
                      <SelectTrigger className="w-44"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="category">By Category</SelectItem>
                        <SelectItem value="consumption_high">Consumption: High to Low</SelectItem>
                        <SelectItem value="consumption_low">Consumption: Low to High</SelectItem>
                        <SelectItem value="value_high">Value: High to Low</SelectItem>
                        <SelectItem value="value_low">Value: Low to High</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {/* Summary */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                  <Card className="bg-purple-50">
                    <CardContent className="pt-4">
                      <p className="text-sm text-purple-600">Total Items</p>
                      <p className="text-2xl font-bold text-purple-700">{reportSummary.total_items || 0}</p>
                    </CardContent>
                  </Card>
                  <Card className="bg-red-50">
                    <CardContent className="pt-4">
                      <p className="text-sm text-red-600">Total Consumption</p>
                      <p className="text-2xl font-bold text-red-700">{reportSummary.total_consumption?.toFixed(2) || 0}</p>
                    </CardContent>
                  </Card>
                  <Card className="bg-green-50">
                    <CardContent className="pt-4">
                      <p className="text-sm text-green-600">Total Value</p>
                      <p className="text-2xl font-bold text-green-700">{formatCurrency(reportSummary.total_value)}</p>
                    </CardContent>
                  </Card>
                  <Card className="bg-blue-50">
                    <CardContent className="pt-4">
                      <p className="text-sm text-blue-600">Avg Daily</p>
                      <p className="text-2xl font-bold text-blue-700">{formatCurrency(reportSummary.avg_daily_value)}</p>
                    </CardContent>
                  </Card>
                </div>

                {reportData.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <BarChart3 className="w-12 h-12 mx-auto mb-2 text-gray-400" />
                    <p>No data for selected period</p>
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Item</TableHead>
                        <TableHead>Category</TableHead>
                        <TableHead>Company</TableHead>
                        <TableHead className="text-right">Total Consumption</TableHead>
                        <TableHead className="text-right">Avg Daily</TableHead>
                        <TableHead className="text-right">Total Value</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {reportData.map((item) => (
                        <TableRow key={item.item_id}>
                          <TableCell className="font-medium">{item.item_name}</TableCell>
                          <TableCell><Badge variant="outline">{item.category_name}</Badge></TableCell>
                          <TableCell>{item.company}</TableCell>
                          <TableCell className="text-right">{item.total_consumption?.toFixed(2)}</TableCell>
                          <TableCell className="text-right">{item.avg_daily?.toFixed(2)}</TableCell>
                          <TableCell className="text-right font-bold text-red-600">
                            {formatCurrency(item.total_value)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}
